import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { GetJson, PutJson, PostJson, DeleteJson } from '@awp/helpers/fetchReact';
import PropTypes from 'prop-types';

import { REST_API, FORM_MODE, Permissions } from '@awp/constants';
import * as Yup from 'yup';

import {
  selectAllHazardTrendDropDown,
  currentWeatherObservation,
  setEditing,
  setWeatherObservation,
} from '@stores/hazardForecastSlice';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import { selectAvalancheTechnicians } from '@awp/stores/dropdownsSlice';
import { getUtcToLocal, convLocalToIsoString, isValueEmpty, isAvalancheSeason } from '@awp/helpers/utilsReact';
import { Card, Button, Spinner, Container, Row, Col } from 'react-bootstrap';

import {
  FormTextArea,
  FormSelect,
  FormDatePicker,
  FormCheckbox,
  FormDatePickerWithChange,
  FormGroupCheckbox,
} from '@components/Common/AwpFormInputs';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import { hasAccess } from '@components/Authorize';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import { format as formatNumber } from '@awp/helpers/numFormat';

import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
const sotTitleStyle = {
  paddingTop: '0.6rem',
  borderTop: '0.063rem solid #bdc5cc',
  borderLeft: '0.063rem solid #bdc5cc',
  borderRight: '0.063rem solid #bdc5cc',
  paddingBottom: '0.6rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // fontSize: '70%',
};
const sotColStyle = {
  borderTop: '0.063rem solid #bdc5cc',
  borderLeft: '0.063rem solid #bdc5cc',
  borderRight: '0.063rem solid #bdc5cc',
  paddingTop: '0.2rem',
  paddingBottom: '0.2rem',
  // fontSize: '70%',
};
const blankColumn = {};
const columnLeft = {
  paddingLeft: '0.2rem',
};

const SupportWeatherObservationEdit = ({
  setMode,
  mode,
  eventId,
  setLoading,
  loading,
  wsObservationLoading,
  setWsObservationLoading,
  refreshingWsObservation,
  setRefreshingWsObservation,
  getForecastWeatherObs,
  fullData,
  setEventTypeToActOn,
}) => {
  const dispatch = useDispatch();
  const selectedWeatherObservation = useSelector(currentWeatherObservation);
  const hazardTrendList = useSelector(selectAllHazardTrendDropDown);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const defaultDto = {
    startTimestamp: null,
    endTimestamp: new Date(),
    isStandard: false,
    excludeList: [],
  };

  const [dto, setDto] = useState(defaultDto);
  const [eventErrors, setEventErrors] = useState(null);

  const readOnly = mode !== FORM_MODE.EDIT && mode !== FORM_MODE.CREATE;
  const buttonLabel = 'Save';

  const title = mode === FORM_MODE.EDIT ? 'Edit Weather Observations' : 'Weather Observations';
  useEffect(() => {
    if (mode !== FORM_MODE.CREATE && selectedWeatherObservation) {
      setDto(entityToDto(selectedWeatherObservation));
    }
  }, []);
  useEffect(() => {
    if (refreshingWsObservation) {
      getForecastWeatherObs('refresh', dto);
    }
  }, [refreshingWsObservation, dto]);

  const entityToDto = (entity) => {
    let _excludeList = [];
    if (entity && entity.WeatherObservationRows && entity.WeatherObservationRows.length > 0) {
      entity.WeatherObservationRows.map((r) => {
        if (r.IsExclude === 'Y' && !_excludeList.includes(r.WeatherStationId + ''))
          _excludeList.push(r.WeatherStationId + '');
      });
    }

    const dtoTemp = {
      isStandard: entity.IsStdWeatherObsOnly == 'Y' ? true : false,
      startTimestamp: entity.PrecipAccumulationStartTimestamp
        ? new Date(getUtcToLocal(entity.PrecipAccumulationStartTimestamp))
        : null,
      endTimestamp: entity.ObservationSnapshotTimestamp
        ? new Date(getUtcToLocal(entity.ObservationSnapshotTimestamp))
        : null,
      excludeList: _excludeList,
    };
    return dtoTemp;
  };
  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);
    let entity = { ...selectedWeatherObservation };
    entity.PrecipAccumulationStartTimestamp = dto.startTimestamp
      ? convLocalToIsoString(new Date(dto.startTimestamp))
      : null;
    entity.ObservationSnapshotTimestamp = dto.endTimestamp ? convLocalToIsoString(new Date(dto.endTimestamp)) : null;
    entity.IsStdWeatherObsOnly = dto.isStandard ? 'Y' : 'N';
    if (dto.excludeList && dto.excludeList.length > 0) {
      let wsRows = [];
      if (entity.WeatherObservationRows) {
        entity.WeatherObservationRows.map((r) => {
          let _r = { ...r };
          if (dto.excludeList.includes(r.WeatherStationId + '')) {
            _r.IsExclude = 'Y';
          } else {
            _r.IsExclude = 'N';
          }
          wsRows.push(_r);
        });
        entity.WeatherObservationRows = wsRows;
      }
    }
    if (mode === FORM_MODE.CREATE) {
      createWeatherObservation(setSubmitting, setErrors, entity);
    } else if (mode === FORM_MODE.EDIT) {
      updateWeatherObservation(setSubmitting, resetForm, setErrors, entity);
    }
  };
  const createWeatherObservation = (setSubmitting, setErrors, dto) => {
    const url = REST_API.HAZARD_FORECAST_WEATHER_OBSERVATION;
    PostJson(url, dto)
      .then(async (response) => {
        if (response.ok) {
          const entity = await response.json();
          const dtoTemp = entityToDto(entity);
          dispatch(setWeatherObservation(entity));
          setDto(dtoTemp);
          toast.success('Weather observation has been created');
          setMode(FORM_MODE.VIEW);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEventErrors({ Entity: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const updateWeatherObservation = (setSubmitting, resetForm, setErrors, hzDto) => {
    const url = REST_API.HAZARD_FORECAST_WEATHER_OBSERVATION + hzDto.EventId;
    PutJson(url, hzDto)
      .then(async (response) => {
        if (response.ok) {
          toast.success('Weather observation has been updated');
          const entity = await response.json();
          const dtoTemp = entityToDto(entity);
          dispatch(setWeatherObservation(entity));
          setDto(dtoTemp);
          setMode(FORM_MODE.VIEW);
          resetForm();
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEventErrors({ Entity: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const deleteWeatherObservation = () => {
    const url = REST_API.HAZARD_FORECAST_WEATHER_OBSERVATION + selectedWeatherObservation.EventId;

    DeleteJson(url, {})
      .then(async (response) => {
        if (response.ok) {
          toast.success('Weather observation has been deleted');
          dispatch(
            setWeatherObservation(null), //clean previous cache
          );
          setMode(FORM_MODE.VIEW);
        } else if (response.status === 412) {
          const errors = await response.json();
          if (errors.entity) setEventErrors({ Analysis: errors.entity });
        }
      })
      .finally(() => {
        //setSubmitting(false);
      });
  };
  const handleBtnClose = (props) => {
    props.resetForm();

    if (mode === FORM_MODE.CREATE) {
      dispatch(setWeatherObservation(null));
      return;
    }
    setWsObservationLoading(true);
    const urlWeatherObservation = REST_API.HAZARD_FORECAST_WEATHER_OBSERVATION + eventId;
    GetJson(urlWeatherObservation)
      .then((response) => response.json())
      .then((entityWeatherObservation) => {
        if (entityWeatherObservation) dispatch(setWeatherObservation(entityWeatherObservation));
      })
      .finally(() => {
        setWsObservationLoading(false);
        setMode(FORM_MODE.VIEW);
      });
    setMode(FORM_MODE.VIEW);
  };
  const onClickDelete = () => {
    setShowDeleteConfirm(true);
  };
  const getObsValue = (obs) => {
    if (isValueEmpty(obs)) return '';
    let n = obs.ObservedCharacterValue.slice(0, 15);
    return isValueEmpty(obs.ObservationType.DisplayFormat)
      ? obs.ObservedCharacterValue
      : formatNumber(n, obs.ObservationType.DisplayFormat);
  };
  const Buttons = ({ props }) => {
    return mode === FORM_MODE.EDIT || mode === FORM_MODE.CREATE ? (
      <>
        <div className="mt-2 mr-3" style={{ float: 'left' }}>
          {!showDeleteConfirm && !showCancelConfirm && (
            <Button
              className="mr-2"
              variant="outline-primary"
              disabled={refreshingWsObservation || wsObservationLoading || props.isSubmitting}
              onClick={() => {
                setShowCancelConfirm(true);
              }}
            >
              {'Cancel'}
            </Button>
          )}

          {!showDeleteConfirm && !showCancelConfirm && hasAccess([Permissions.WriteAvalancheHazard]) && (
            <>
              <Button
                type="submit	"
                variant="primary"
                disabled={
                  refreshingWsObservation ||
                  wsObservationLoading ||
                  !props.isValid ||
                  props.isSubmitting ||
                  !selectedWeatherObservation ||
                  !selectedWeatherObservation.WeatherObservationRows ||
                  selectedWeatherObservation.WeatherObservationRows.length === 0
                }
              >
                {props.isSubmitting ? (
                  <>
                    <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  <span>{buttonLabel}</span>
                )}
              </Button>
            </>
          )}
          {!showDeleteConfirm &&
            !showCancelConfirm &&
            hasAccess([Permissions.WriteAvalancheHazard]) &&
            mode === FORM_MODE.EDIT && (
              <Button
                className="ml-2"
                variant="danger"
                disabled={
                  refreshingWsObservation ||
                  wsObservationLoading ||
                  !props.isValid ||
                  props.isSubmitting ||
                  !selectedWeatherObservation ||
                  !selectedWeatherObservation.WeatherObservationRows ||
                  selectedWeatherObservation.WeatherObservationRows.length === 0
                }
                onClick={() => {
                  onClickDelete();
                }}
              >
                Delete
              </Button>
            )}
          {showCancelConfirm && (
            <AwpYesNoDialog
              action={() => handleBtnClose(props)}
              setShowYesNoConfirm={setShowCancelConfirm}
              msg={'Are you sure you want to cancel?'}
              props={{}}
            />
          )}
          {showDeleteConfirm && (
            <AwpYesNoDialog
              action={deleteWeatherObservation}
              setShowYesNoConfirm={setShowDeleteConfirm}
              props={props}
            />
          )}
        </div>
      </>
    ) : (
      mode === FORM_MODE.VIEW && (
        <Button
          className="mr-2"
          variant="outline-primary"
          onClick={() => {
            setShowCancelConfirm(false);
            setMode(FORM_MODE.EDIT);
          }}
        >
          {'Edit'}
        </Button>
      )
    );
  };

  Buttons.propTypes = {
    title: PropTypes.string,
    props: PropTypes.any,
    resetForm: PropTypes.func,
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    dirty: PropTypes.bool,
  };
  const validateDate = (start, end) => {
    if (!validateStartDate(start, end) || !validateEndDate(end, start)) return false;
    return true;
  };
  const validateStartDate = (value, endTs) => {
    if (!value) return true;
    if (new Date(value) > new Date()) return false;
    if (!isAvalancheSeason(value)) return false;
    if (endTs && value >= endTs) return false;
    return true;
  };
  const handleStartDate = useCallback(
    (e, props) => {
      if (isValueEmpty(e)) {
        setTimeout(() => {
          props.setFieldValue('startimestamp', null);
        }, 1000);
      } else {
        setTimeout(() => {
          props.setFieldValue('startimestamp', new Date(e));
        }, 1000);
      }
      let _dto = { ...dto };
      _dto.startTimestamp = isValueEmpty(e) ? null : new Date(e);
      setDto(_dto);
      if (!validateDate(e, props.values.endTimestamp)) return;
      setRefreshingWsObservation(true);
    },
    [dto],
  );

  const validateEndDate = (value, startTs) => {
    if (new Date(value) > new Date()) return false;
    if (!isAvalancheSeason(value)) return false;
    if (startTs && value <= startTs) return false;
    return true;
  };
  const handleEndDate = useCallback(
    (e, props) => {
      let dt = e;
      if (isValueEmpty(dt)) {
        props.setFieldValue('endTimestamp', null);
      } else {
        //dt = roundEndTimestamp(e);
        props.setFieldValue('endTimestamp', new Date(dt));
      }
      let _dto = { ...dto };
      _dto.endTimestamp = isValueEmpty(dt) ? null : new Date(dt);
      setDto(_dto);
      if (!validateDate(props.values.startTimestamp, dt)) return;
      setRefreshingWsObservation(true);
    },
    [dto],
  );
  const adjustEndTimestamp = (endTimestamp) => {
    const sixPM = new Date(endTimestamp);
    sixPM.setHours(18, 0, 0, 0); // Set time to 18:00:00.000

    if (endTimestamp < sixPM) {
      endTimestamp.setHours(6, 0, 0, 0); // Set time to 06:00:00.000
    } else {
      endTimestamp.setHours(18, 0, 0, 0); // Set time to 18:00:00.000
    }

    return endTimestamp;
  };
  const roundEndTimestamp = (endTimestamp) => {
    return endTimestamp; //endTimestamp.setMinutes(0, 0);
  };

  const handleIsStandard = useCallback(
    (props) => {
      props.setFieldValue('isStandard', !props.values.isStandard);
      let _dto = { ...dto };
      _dto.isStandard = !props.values.isStandard;
      setDto(_dto);
      if (!props.values.isStandard) {
        let newEnd = isValueEmpty(props.values.endTimestamp) ? null : adjustEndTimestamp(props.values.endTimestamp);
        props.setFieldValue('endTimestamp', newEnd);
        _dto.endTimestamp = newEnd;
        if (!validateDate(props.values.startTimestamp, newEnd)) return;
      }
      setRefreshingWsObservation(true);
    },
    [dto],
  );

  const handleIsExclude = useCallback(
    (e, props) => {
      const val = e.target.value;
      const chk = e.target.checked;
      const wsId = e.target.id + '';
      let _dto = { ...dto };
      if (chk) {
        if (!_dto.excludeList) _dto.excludeList = [];
        if (!_dto.excludeList.includes(wsId + '')) _dto.excludeList.push(wsId + '');
      } else {
        let newList = [];
        _dto.excludeList.map((ws) => {
          if (ws + '' !== wsId + '') newList.push(ws + '');
        });
        _dto.excludeList = newList;
      }
      props.setFieldValue('excludeList' + _dto.excludeList);
      setDto(_dto);
      setRefreshingWsObservation(true);
    },
    [dto],
  );
  return (
    <>
      {loading ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : (
        <>
          <Formik
            enableReinitialize
            validateOnMount
            validateOnBlur
            validateOnChange
            initialValues={dto}
            initialTouched={{
              startTimestamp: true,
              endTimestamp: true,
            }}
            validationSchema={Yup.object({
              startTimestamp: Yup.date()
                .nullable()
                .test(
                  'startTimestamp',
                  'Start Date Time must be within the current Avalanche season',
                  (value, context) => {
                    if (isValueEmpty(value)) return true;
                    return isAvalancheSeason(value);
                  },
                )
                .test('compareStartTimestamp', 'Start Date Time must be less than End Date Time', (value, context) => {
                  if (isValueEmpty(value)) return true;
                  return value < context.parent.endTimestamp;
                }),
              endTimestamp: Yup.date()
                .nullable()
                .required('End Date Time is required')
                .test('futureDate', 'End Date Time cannot be in the future', (value) => {
                  return new Date(value) < new Date();
                })
                .test('endTimestamp', 'End Date Time must be within the current Avalanche season', (value, context) => {
                  return isAvalancheSeason(value);
                })
                .test('compareEndTimestamp', 'End Date Time must be greater than Start Date Time', (value, context) => {
                  if (isValueEmpty(context.parent.startTimestamp)) return true;
                  return value > context.parent.startTimestamp;
                })
                .test(
                  'standardEndTimestamp',
                  'End Date Time must be either 6:00 or 18:00 if standard observation is selected',
                  (value, context) => {
                    if (!context.parent.isStandard) return true;
                    return value === adjustEndTimestamp(value);
                  },
                ),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <>
                <Form>
                  <Card
                    border="light"
                    className="page-card"
                    style={{ border: '0px', boxShadow: '0px 0px 0px #000000' }}
                  >
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <div className="customWidthDatePicker">
                            <FormDatePickerWithChange
                              readOnly={readOnly}
                              label="Accumulated SnowFall & Precipitation Start Date Time"
                              id="startTimestamp"
                              name="startTimestamp"
                              showTimeSelect={true}
                              showTime={{ isEnabled: true }}
                              timeFormat="HH:mm"
                              timeIntervals={60}
                              onChange={(e) => {
                                setTimeout(() => {
                                  handleStartDate(e, props);
                                }, 2000);
                              }}
                              dateFormat={['yyyy-MM-dd HH:mm']}
                              disabled={props.isSubmitting || refreshingWsObservation}
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="customWidthDatePicker">
                            <FormDatePickerWithChange
                              readOnly={readOnly}
                              label="End Date Time (required)"
                              id="endTimestamp"
                              name="endTimestamp"
                              showTimeSelect={true}
                              showTime={{ isEnabled: true }}
                              timeFormat="HH:mm"
                              timeIntervals={60}
                              onChange={(e) => {
                                setTimeout(() => {
                                  handleEndDate(e, props);
                                }, 3000);
                              }}
                              dateFormat={['yyyy-MM-dd HH:mm']}
                              disabled={props.isSubmitting || refreshingWsObservation}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={7}>
                          <FormCheckbox
                            readOnly={readOnly}
                            id="isStandard"
                            name="isStandard"
                            label="Standard Observations Only"
                            disabled={props.isSubmitting || refreshingWsObservation}
                            onChange={() => {
                              handleIsStandard(props);
                            }}
                          />
                        </Col>
                      </Row>
                      <br />
                      {refreshingWsObservation && (
                        <Row>
                          <Col md={12}>
                            <Container>
                              <Spinner animation="border" />
                            </Container>
                          </Col>
                        </Row>
                      )}
                      <div className="overflow-auto">
                        <div style={{ width: '110rem', marginLeft: '1rem' }}>
                          {!refreshingWsObservation && (
                            <Row className="font-weight-bold">
                              <Col xs={1} style={sotTitleStyle}>
                                {'EXCLUDE'}
                              </Col>
                              <Col xs={2} style={sotTitleStyle}>
                                {'WEATHER STATION'}
                              </Col>
                              <Col xs={2} style={sotTitleStyle}>
                                {'SNOWFALL (cm)'}
                              </Col>
                              <Col xs={3} style={sotTitleStyle}>
                                {'PRECIPITATION (mm)'}
                              </Col>
                              <Col xs={2} style={sotTitleStyle}>
                                {'TEMPERATURE ('}&#8451;{')'}
                              </Col>
                              <Col xs={2} style={sotTitleStyle}>
                                {'WIND (km/h and '}&#176;{')'}
                              </Col>
                            </Row>
                          )}
                          {!refreshingWsObservation && (
                            <Row className="font-weight-bold">
                              <Col xs={1} style={sotColStyle}>
                                {''}
                              </Col>
                              <Col xs={2} style={sotColStyle}>
                                {''}
                              </Col>
                              <Col xs={2} style={sotColStyle}>
                                <Row>
                                  <Col xs={4}>{'HStd'}</Col>
                                  <Col xs={4}>{'HS'}</Col>
                                  <Col xs={4}>{'Accum'}</Col>
                                </Row>
                              </Col>
                              <Col xs={3} style={sotColStyle}>
                                <Row>
                                  <Col xs={3}>{'Hrly'}</Col>
                                  <Col xs={3}>{'12 Hour'}</Col>
                                  <Col xs={3}>{'24 Hour'}</Col>
                                  <Col xs={3}>{'Accum'}</Col>
                                </Row>
                              </Col>
                              <Col xs={2} style={sotColStyle}>
                                <Row>
                                  <Col xs={4}>{'Min'}</Col>
                                  <Col xs={4}>{'Present'}</Col>
                                  <Col xs={4}>{'Max'}</Col>
                                </Row>
                              </Col>
                              <Col xs={2} style={sotColStyle}>
                                <Row>
                                  <Col xs={4}>{'Speed'}</Col>
                                  <Col xs={4}>
                                    {'Speed'}
                                    <br />
                                    {'(Max)'}
                                  </Col>
                                  <Col xs={4}>{'Dir'}</Col>
                                </Row>
                              </Col>
                            </Row>
                          )}
                          {!refreshingWsObservation &&
                            selectedWeatherObservation &&
                            (!selectedWeatherObservation.WeatherObservationRows ||
                              selectedWeatherObservation.WeatherObservationRows.length === 0) && (
                              <Row>
                                <Col style={sotColStyle} md={12}>
                                  {' '}
                                  no records found
                                </Col>
                              </Row>
                            )}
                          {!refreshingWsObservation &&
                            selectedWeatherObservation &&
                            selectedWeatherObservation.WeatherObservationRows &&
                            selectedWeatherObservation.WeatherObservationRows.map((wob, i) => {
                              if (!wob || !wob.WeatherStationId) return;
                              return (
                                <Row key={'wobs_row_key_' + i + ''} className="bg-white">
                                  <Col xs={1} style={sotColStyle}>
                                    <FormGroupCheckbox
                                      readOnly={readOnly}
                                      name="excludeList"
                                      id={wob.WeatherStationId}
                                      onChange={(e) => {
                                        handleIsExclude(e, props);
                                      }}
                                      exclude={dto.excludeList}
                                      value={dto.excludeList}
                                    />
                                  </Col>
                                  <Col xs={2} style={sotColStyle}>
                                    {wob.WeatherStationNumber + ' - ' + wob.WeatherStationName}
                                  </Col>
                                  <Col xs={2} style={sotColStyle}>
                                    <Row>
                                      <Col xs={4}>{getObsValue(wob.Hstd)}</Col>
                                      <Col xs={4}>{getObsValue(wob.Hs)}</Col>
                                      <Col xs={4}>{getObsValue(wob.HsAccum)}</Col>
                                    </Row>
                                  </Col>
                                  <Col xs={3} style={sotColStyle}>
                                    <Row>
                                      <Col xs={3}>{getObsValue(wob.Hrly)}</Col>
                                      <Col xs={3}>{getObsValue(wob.Hr12)}</Col>
                                      <Col xs={3}>{getObsValue(wob.Hr24)}</Col>
                                      <Col xs={3}>{getObsValue(wob.HrAccum)}</Col>
                                    </Row>
                                  </Col>
                                  <Col xs={2} style={sotColStyle}>
                                    <Row>
                                      <Col xs={4}>{getObsValue(wob.TempMin)}</Col>
                                      <Col xs={4}>{getObsValue(wob.TempPresent)}</Col>
                                      <Col xs={4}>{getObsValue(wob.TempMax)}</Col>
                                    </Row>
                                  </Col>
                                  <Col xs={2} style={sotColStyle}>
                                    <Row>
                                      <Col xs={4}>{getObsValue(wob.WindSpeed)}</Col>
                                      <Col xs={4}>{getObsValue(wob.WindSpeedMax)}</Col>
                                      <Col xs={4}>{getObsValue(wob.WindDir)}</Col>
                                    </Row>
                                  </Col>
                                </Row>
                              );
                            })}
                          {!refreshingWsObservation && <Row style={{ borderTop: '0.063rem solid #bdc5cc' }}></Row>}
                        </div>
                        <br />
                      </div>

                      <br />
                      <Buttons title=" " props={props} />
                    </Card.Body>
                  </Card>
                </Form>
              </>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setEventErrors} errors={eventErrors} />
        </>
      )}
    </>
  );
};

SupportWeatherObservationEdit.propTypes = {
  // setMode: PropTypes.func,
  // mode: PropTypes.string,
  // setEventId: PropTypes.func,
  // eventId: PropTypes.number,
  // setLoading: PropTypes.func,
  // loading: PropTypes.bool,
  // setFieldValue: PropTypes.func,
  // validateField: PropTypes.func,
  // setFieldTouched: PropTypes.func,
  // values: PropTypes.any,
  // resetForm: PropTypes.func,
  // fullData: PropTypes.array,
  // setEventTypeToActOn: PropTypes.func,
};

export default SupportWeatherObservationEdit;
